/**
 * @module core/functionality/form
 */

const NAME = 'form';

import CoreCollection from '../lib/netx-collection';
import FormErrorListView from '@netx/core-app/views/formErrorListView';

import {unescapeAttributeName} from '../lib/underscore-netx';

import 'core-libs/formValidation-netx';

export const Props = {
	/**
	 * Do anything you want when a field loses focus
	 * @method
	 * @param {event} e jQuery event
	 */
	blurred(e) {
		//
	},

	/**
	 * To support arbitrary re-rendering of the form at any time,
	 * we cache all form values in _.formValues during preRender
	 * and restore them (one by one) in postRender.
	 * @see restoreCachedFormValue
	 * @see flushCachedFormValues
	 * @method
	 */
	cacheFormValues() {
		//this._formValues = this.formChanged ? this.formValues() : {};
		// TODO: this sometimes fails, because subclasses override .formValues() in all sorts
		// of different ways.  I had a note in here to not do that ... but it's been pretty useful.
		//
		// I think the solution is to call the non-overriden method, Netx.Views.FormView.protytpe.formValues(),
		// for just this specific case:
		this._formValues = this.formChanged ? this.formValues() : {};
	},

	/**
	 * You may want to override this.cacheQueries(), to cache more queries.
	 * By default it caches jQuery queries, by name, for all form inputs.  They are stored in the object this.inputs.
	 * Example: if the template contains a form field named "address", it will be cached in this.inputs.$address .
	 * (The leading $ is a naming convention to indicate a jQuery query.)
	 * @method
	 */
	cacheQueries() {
		this.inputs = {};

		_.each(this.$(':input'), (el) => {
			// if element doesn't have a name, use id; if not that, fuggedaboutit.
			const elName = el.name ? el.name : el.id;

			if (el.type == 'radio' || el.type == 'checkbox') {
				// Radios always come in groups, checkboxes sometimes do too.
				// If we haven't cached the complete group yet,
				if (!this.inputs[`$${elName}`]) {
					// cache it now.
					this.inputs[`$${elName}`] = this.$(`:input[name="${elName}"]`);
				}
				return;
			}
			// or if the element is unnamed, discard it.
			if (elName) {
				this.inputs[`$${elName}`] = this.$(el);
			}
		});
	},

	/**
	 * this.checkForm() is called at render, and on every form change.
	 * If your form needs to manipulate itself based on changes, override this method to do that.
	 * By default, it enables or disables the submit button based on the return value of this.submitReady();
	 * @method
	 * @param {event} e jQueryEvent (optional)
	 */
	checkForm(e) {
		// if this form hasn't ever rendered, there's nothing we can do here.
		if (!this.$submit || !this.$submit.length) return;

		if (this.submitReady()) {
			this.$submit.enable();
			if (this.$submit.data('enabled-label')) {
				this.$submit.html(this.$submit.data('enabled-label'));
			}
		} else {
			this.$submit.disable();
			if (this.$submit.data('disabled-label')) {
				this.$submit.html(this.$submit.data('disabled-label'));
			}
		}
	},

	// /**
	//  * this.clearForm() clears out all values in the form.
	//  * http://stackoverflow.com/questions/680241/resetting-a-multi-stage-form-with-jquery
	//  * @method
	//  * @deprecated
	//  * @todo This is all handled in our reset wrapper above.
	//  */
	// clearForm() {
	// 	console.warn('formView.clearForm deprecated, gone in 8.13');
	// 	this.$el.find('input:text, input:password, input:file, select, textarea').val('');
	// 	this.$el.find('input:radio, input:checkbox').removeAttr('checked').removeAttr('selected');
	// 	// TODO: reset changes?
	// }

	/**
	 * Is called when this.eventStr + ":error" is triggered
	 * @method
	 * @param {string} error Error string
	 */
	failure(error, thing) {
		// Check that the failure was for us (comes from <eventStr>:error)
		// Our pattern has been to have the model/collection as the second argument passed back from the success event
		if (thing) {
			if (this.model && thing.isModel) {
				if (thing !== this.model) {
					return;
				}
			} else if (this.collection && thing.isCollection) {
				if (thing !== this.collection) {
					return;
				}
			}
		}

		if (this.submitting) {
			this.submitting.rejectWith(this, arguments);
			delete this.submitting;
		}
		this.$el.removeClass('form-submitting');
		this.$('.form-submitting').hide();
	},

	/**
	 * A new input field was added
	 * @method
	 * @param e Field added event (nx)
	 */
	fieldAdded(e) {
		const validator = this.validator;
		const $target = $(e.target);
		if (validator.enabled && validator.plugin) {
			validator.plugin.addField($target);
			if (this.revalidateFieldOnAdd) {
				validator.plugin.revalidateField($target);
			}
		}
		// Add reference to the cached queries
		if (this.inputs) {
			this.inputs['$' + e.target.name] = $target;
		}
	},

	/**
	 * An input field is being modifed, assume it will be non recoverable and remove from
	 * the validation instance (will be added back in `this.fieldMutated` if the proper event is fired by the view in charge)
	 * @method
	 * @param e Field changing event (nx)
	 */
	fieldMutating(e) {
		const validator = this.validator;

		validator.enabled &&
			validator.plugin &&
			validator.plugin._cacheFields[e.target.name] &&
			validator.plugin.removeField($(e.target));
	},

	/**
	 * Should be fired right after the input field has been mutated (see `this.fieldMutating`)
	 * @method
	 * @param e Field changed event (nx)
	 */
	fieldMutated(e) {
		const validator = this.validator;
		const $target = $(e.target);
		if (validator.enabled && validator.plugin) {
			validator.plugin.addField($target).revalidateField($target);
		}
		// Add reference back to the cached queries
		if (this.inputs) {
			this.inputs['$' + e.target.name] = $target;
		}
	},

	/**
	 * Should be fired right before the filed is removed from the DOM
	 * Important that the input is still in the DOM so we can properyly remove validation
	 * @method
	 * @param e Field removed event (nx)
	 */
	fieldRemoved(e) {
		const validator = this.validator;

		validator.enabled &&
			validator.plugin &&
			validator.plugin._cacheFields[e.target.name] &&
			validator.plugin.removeField($(e.target));

		// Remove cached query
		if (this.inputs) {
			delete this.inputs['$' + e.target.name];
		}
	},

	/**
	 * A field has been deemed invalid
	 * @method
	 * @param {event} e jQuery event
	 * @param {object} fvf formValidation field object
	 */
	fieldValidationFailure(e, fvf) {},

	/**
	 * A field has been deemed valid
	 * @method
	 * @param {event} e jQuery event
	 * @param {object} fvf formValidation field object
	 */
	fieldValidationSuccess(e, fvf) {
		// If we have submitted before then we obviously had some errors
		if (this.errorCache.length && this.submitted) {
			this.errorCache.remove(fvf.element.attr('name'));
		}
	},

	/**
	 * Clear all form values we may have cached during a render
	 * @method
	 * @see cacheFormValues
	 * @see restoreCachedFormValue
	 */
	flushCachedFormValues() {
		delete this._formValues;
	},

	/**
	 * this.focus() is called when the form is shown or rendered; override it to set form focus how you like it.
	 * The default does this: http://stackoverflow.com/questions/267615/focusing-first-control-in-the-form-using-jquery
	 * @method
	 * @param {string} selector jQuery selector string
	 */
	focus(selector) {
		if (selector == null) {
			this.$('form:not(.filter) :input:visible:enabled:first').focus();
		} else {
			this.$(selector).focus();
		}
	},

	/**
	 * The form was submitted but was invalid
	 * @method
	 * @param {event} e jQuery event
	 * @fires (this.eventStr):validation:failure
	 * @fires module:notification#notification
	 */
	formValidationFailure(e) {
		// Set errors
		this.errorCache.set(
			_.map(this.getValidationErrors(), (input) => {
				return {
					id: input.name,
					name: input.name,
					input: $(input),
				};
			}),
		);

		dispatcher.trigger(`${this.eventStr}:validation:failure`, this);
		this.submitted = true;
		// We will need to rethink this as you cannot read the whole message in that little notification box.
		dispatcher.trigger('notification', {
			module: 'app',
			title: i18n.t('i18n.error'),
			description: i18n.t('i18n.invalidForm'),
			type: 'danger',
		});
	},

	/**
	 * Form was successfully validated
	 * @method
	 * @param {event} e jQuery event
	 */
	formValidationSuccess(e) {
		// forward the event
		dispatcher.trigger(`${this.eventStr}:validation:success`, this);
	},

	/**
	 * this.formValues() gathers the names and values of all the form inputs into one simple JS object for submission to a submit event handler.
	 * This should not need to be overridden -- if it's not working, let's fix it.
	 * @method
	 * @return {object} Object containing input form values
	 */
	formValues() {
		// HACK: the initial query caching can fail for the assetAttributes view, because its fields
		// all have templates that are still loading.  We solved that for childViews ... so attrs should
		// just be converted to childViews.  For now, make sure we get everything by caching once more.
		//
		// TODO: this shouldn't be necessary now that templates are preloaded.  Remove this?
		// No, so much depends on this behavior apparently - that is how unknown (new) inputs magically found their way in to this.inputs.
		this.cacheQueries();

		const values = {};
		_.each(this.inputs, (i, $idx, all) => {
			// TODO: there's some oddness here:
			// At this point, i is a jquery selector,
			// but we're also using $(i) below -- which works, but is not necessary.
			// Also: use .is() to determine attr type & state, instead of all that .attr/.prop stuff.
			// In general this method needs cleanup & better doc, but it's working, so ...

			// '$foo' -> 'foo'
			let idx = $idx.slice(1);

			// Skip any auto-generated form elements created by select2 -- they don't contain our data.
			// TODO: do this in cacheQueries() instead.  (Never even cache these.)
			if (idx.match(/s2id_autogen\d/)) {
				return;
			}

			// If we have multiple inputs with array styled names (like attribute[0], attribute[1]);
			let match = idx.match(/\[(.*?)\]/g);

			if (!_.isNull(match)) {
				if (match.length === 1) {
					idx = idx.replace(/\[(.*?)\]/, '');
					if (!values[idx]) {
						values[idx] = _.isNumeric(idx) ? [] : {};
					}
					// eslint-disable-next-line no-useless-escape
					values[idx][match[0].replace(/[\[\]']+/g, '')] =
						i.attr('type') == 'checkbox'
							? i.prop('checked')
							: i.attr('type') == 'radio'
							? i.filter(':checked').val()
							: i.val();
					return;
				} else {
					// (like attribute[thing1][thing], attribute[thing2][thing])
					const topLevelName = idx.replace(match.join(''), '');
					if (_.isUndefined(values[topLevelName])) {
						values[topLevelName] = {};
					}
					_.reduce(
						match,
						(memo, name, index, all) => {
							// eslint-disable-next-line no-useless-escape
							name = name.replace(/[\[\]']+/g, '');
							if (_.isUndefined(memo[name])) {
								memo[name] =
									index + 1 < all.length
										? {}
										: i.attr('type') == 'checkbox'
										? i.prop('checked')
										: i.attr('type') == 'radio'
										? i.filter(':checked').val()
										: i.val();
							}
							return memo[name];
						},
						values[topLevelName],
					);
					return;
				}
			}

			// If we have broken a multiselect into individual checkboxes, save only the box that's checked.
			match = idx.match(/-checkbox(\d+)$/);
			// DEVNOTE: to match normal multiselect behavior, we need 'null' instead of empty array for no selection.
			if (!_.isNull(match)) {
				if (!values[match[1]]) {
					values[match[1]] = '';
				}
				if ($(i).prop('checked')) {
					// convert the csv to an array, append to it, then convert back
					values[match[1]] = _.arrayToCsv(
						_.csvToArray(values[match[1]]).concat($(i).val()),
					);
					return;
				} else {
					return;
				}
			}

			// for multiselects, encode the array value as CSV.
			if (i.prop('multiple')) {
				values[idx] = _.arrayToCsv(i.val());
				return;
			}

			// If this is a radio button, find the radio button by this name that's actually checked
			if ($(i).attr('type') == 'radio') {
				const checkedRadio = this.$(':input[name="' + i[0].name + '"]:checked');
				if (checkedRadio.length) {
					i = checkedRadio;
				} else {
					return;
				}
			}

			// If this is a checkbox group, amalgamate the checked values.
			if (i.is(':checkbox') && i.length > 1) {
				const checkedBoxes = this.$(':input[name="' + i[0].name + '"]:checked');
				// convert to array of values:
				values[idx] = checkedBoxes.map(function (elIdx, el) {
					return $(el).val();
				});
				return;
			}

			values[idx] = i.attr('type') == 'checkbox' ? i.prop('checked') : i.val();
		});

		return values;
	},

	/**
	 * Gets a jQuery array of invalid fields
	 * @method
	 * @return {array} Invalid fields
	 */
	getValidationErrors() {
		const validation = this.Plugins.GetInstance('validation');
		if (validation) {
			return validation.getInvalidFields();
		}
		return [];
	},

	/**
	 * Ok, there are some issues with trying to be agnostic and having input/select ui plugins that hide the original element
	 * you still want that hidden element to be validated but it is almost impossible to know (without modifying code each time you add a plugin)
	 * if the element was hidden intentionally (like we do in attribute forms) and when an element was hidden to manipulate the ui only.
	 * Solution: rather than calling hide/show/toggle directly on the elements in the view- pass the elements through here.
	 * It also handles bulk hiding (the validator will look at parents for the data-nx-ignore="validation" attribute as well)
	 * If validation is not turned on for whatever reason the element passed will be sent to where it would have gone
	 * @param {element} input The jQuery element to hide
	 */
	hideField(...input) {
		const inputs = [...input];
		const $form = this.$form;
		const plugins = this.Plugins;
		const validation = this.validator.enabled;

		_.each(inputs, (input) => {
			let $input = $(input);
			if (!$input.is(':input')) {
				$input = $input.find(':input');
			}

			let $formGroup = $input.closest('.form-group');
			if (!$formGroup.length) {
				$formGroup = $input;
			}

			if (validation) {
				_.each($input, (el) => {
					el.name &&
						plugins.Invoke('validation', $form, 'updateStatus', [
							el.name,
							'VALID',
						]);
				});
			}

			$input.nxIgnore('validation');
			$formGroup.hide();
		});
	},

	/**
	 * Tell FormView that a form changed happened.
	 * It sets the formChanged flag, and calls checkForm() to potentially enable the submit button.
	 * Form input change events are forwarded here (see events{} block).
	 * Various other form tricks (like adding/removing inputs from the form) may also need to trigger this.
	 * @method
	 * @param {event} e Change event (optional)
	 */
	inputChanged(e) {
		let $target, changeEvent;
		if (e instanceof jQuery.Event) {
			changeEvent = e;
			$target = this.$(changeEvent.target);
			if (
				$target.nxIgnores('change') ||
				$target.hasClass('select2-search__field')
			) {
				return;
			}
		}
		this.formChanged = true;
		this.trigger('formChanged', $target);
		this.checkForm(changeEvent);
	},

	/**
	 * @method
	 * @return {boolean}
	 */
	isValid() {
		return this.submitReady();
	},

	/**
	 * this.keydown() checks if there are more erros and focus the input with the next error
	 * @method
	 * @param {event} e jQuery event
	 */
	keydown(e) {
		const keyCode = e.keyCode || e.which;
		// Only care if it was an enter key
		if (keyCode === 13 && this.errorCache.length) {
			this.errorCache.at(0).get('input').focus();
		}
	},

	/** @override */
	prepareForm() {
		// Cleanup
		this.$form && this.$form.off();
		this.validator.enabled = false;
		this.Plugins.Destroy('validation');

		if (this.disableCache) {
			// Fresh start
			this.formChanged = false;
			this._formValues = {};
		} else {
			this.cacheFormValues();
		}
	},

	/**
	 * Do not override postRender() without calling the superclass imlementation (this._super ) explicity!
	 * Sets up submit target and calls {@link #cacheQueries} and {@link #checkForm}.
	 * @method
	 */
	renderForm() {
		// Cache
		this.$form = this.$('form:first');
		this.$submit = this.$('[type="submit"]');
		this.$target = this.$('.target');

		/**
		 * Form permissions:
		 * Asset and category models have an 'allows' method that checks their internal permissions
		 * against the current use ID and one of the default permission types (for instance, 'modify').
		 *
		 * If this.model.allows() is a method, and this.matrix is a string,
		 * FormView will test that string using that function, and put the form into 'read-only' mode if the test fails:
		 * the class 'read-only' will be added to the top-level element, and all inputs will be disabled.
		 *
		 * Individual form inputs that need to override this can set the HTML attrbute "data-nx-matrix"
		 * to a different permision name (for instance, 'read') and that permission will be tested instead.
		 */

		// https://jira.netx.net/browse/DAM-9780
		// We do not want to collide with `attributePermissions` and `permissionMatrix`
		// Really, we need to break the attribute code out of formView and let it do what it does best: forms
		let readonly = false;

		this.$el.toggleClass('read-only', readonly);

		let disabledCount = 0;
		let emptyCount = 0;
		let inputCount = 0;
		// TODO: Start using form names and target our searches for inputs to that specific form
		// That way we can start utilizing nested forms without interference
		// Iterate over each input in the form:
		_.each(this.$(':input').not(':button'), (element) => {
			const $element = $(element);
			// Is this input for toggling "append value"?
			const isAppend = $element.hasClass('nx-append-value');

			// Track amount of inputs
			// Omit "append value" toggles
			if (!isAppend) {
				inputCount++;
			}

			const $input = $element;
			// TODO: This code will never get hit because we explicitely set this to fale with no way to change it from the outside
			// Disable the input if we're read-only:
			if (readonly) {
				const disableInput = !$input.nxIgnores('matrix');

				$input
					.prop('disabled', disableInput)
					.closest('.form-group')
					.toggleClass('read-only', disableInput);

				// Track the amount of disabled inputs
				if (disableInput) {
					disabledCount++;
				}
			}

			// restore the value from before render, if any
			const cachedValue = this.restoreCachedFormValue($element) || $input.val();
			const value = cachedValue;
			let hasValue = !_.isUndefined(value);
			if (hasValue) {
				if (_.isArray(value)) {
					hasValue = value.length > 0;
				} else if (_.isString(value)) {
					hasValue = value.trim() != '';
				}
			}
			// Track the amount of empty inputs
			if (!isAppend && !hasValue) {
				emptyCount++;
			}
		});

		// if our form has any backbone tooltips, initialize that piece of Backbone.
		this.$('[data-toggle="tooltip"]').tooltip();

		this.cacheQueries();

		// Validation error list element
		this.$validationErrors = this.$('[data-nx-control="validation-errors"]');
		this.errorCacheView.setElement(this.$validationErrors).render();

		// Are all the fileds disabled
		this.$el.toggleClass(
			'all-fields-disabled',
			!_.isEmpty(this.inputs) && disabledCount >= inputCount,
		);
		// Are all the fields empty
		this.$el.toggleClass(
			'all-fields-empty',
			!_.isEmpty(this.inputs) && emptyCount >= inputCount,
		);

		// DEVNOTE: X7 core code should not be making UX decisions like
		// determining whether a form field gains focus when shown. The
		// theme itself should decide when to utilize a formView's focus() method.
		// this.focus();

		// Most likely cases of this- modals
		// If the element is not visible then neither is any of its inputs
		// We can not get validation on inputs that are not visible
		// So let's wait to check the form until it is shown
		this.whenShown.then(this.checkForm);
	},

	/**
	 * this.reset() is intended to reset the form into a clean initial state.
	 * The actual reset of the form is handled in a wrapper above.
	 * This method is intended to provide child views the ability to reset private
	 * variables, set up complex UI elements, etc.
	 * this.reset() is called automatically when this.show() is called, if the
	 * boolean this.resetOnShow is true and the view is not already visible in the DOM.
	 * @method
	 */
	reset() {
		const $form = this.$('form');
		// Vanilla reset
		$form.length && $form[0].reset();
		// Validator reset - only resets fields that would be validated
		this.validator.enabled &&
			this.Plugins.Invoke('validation', $form, 'resetForm');

		// Other
		this.formChanged = false;
		this.submitted = false;
		// If the submitting promise is still around for some reason
		if (this.submitting) {
			// And it is still in a pending state - reject
			this.submitting.state() === 'pending' && this.submitting.rejectWith(this);
			delete this.submitting;
		}
	},

	/**
	 * Restore a single form value from the cache
	 * @method
	 * @param {object} element jQuery element
	 * @return {mixed} Cached value
	 * @see cacheFormValues
	 * @see flushCachedFormValues
	 */
	restoreCachedFormValue($element) {
		const inputName = $element.attr('name');
		let value;

		if (
			inputName == null ||
			inputName === false ||
			!this._formValues ||
			!_.has(this._formValues, inputName)
		)
			return value;

		// We have to unpack the cache differently for different input types.
		if ($element.prop('multiple')) {
			value = _.csvToArray(this._formValues[inputName]);
			$element.val(value);
			return value;
		}
		if ($element.attr('type') === 'checkbox') {
			value = this._formValues[inputName];
			$element.prop('checked', value);
			return value;
		}

		// If it's a radio, we need to check the correct option in the group
		if ($element.attr('type') === 'radio') {
			value = this._formValues[inputName];
			$element.prop('checked', $element.val() === value);
			return value;
		}

		value = this._formValues[inputName];
		$element.val(value);
		return value;
	},

	/**
	 * this.setForm(values) sets all inputs to the values given.
	 * @method
	 * @param {object} values Form values
	 */
	setForm(values) {
		_.each(values, (v, idx) => this.setValue(idx, v));
	},

	/**
	 * this.setFormValidation() is called at postRender and initializes form validation
	 * @method
	 * @param {element} $form Form element
	 */
	setFormValidation($form) {
		$form = $form || this.$('form[data-nx-validate-form]');

		// Cleanup
		this.Plugins.Destroy('validation');
		this.validator.enabled = false;

		if (!$form.length) return;

		this.validator.enabled = true;
		this.Plugins.Initialize('validation', $form).then((instance) => {
			// Store
			this.validator.plugin = instance;

			// Pipe some events
			_.each(
				['err.form.fv', 'err.field.fv', 'success.form.fv', 'success.field.fv'],
				(event) => {
					$form.off(event).on(event, (...args) => {
						this.trigger(`validation:${event}`, ...args);
					});
				},
			);

			// FV does a weird thing: it catches submit events, preventsDefault() on them,
			// and detects if they were due to a submit button click or not.
			// BUT it doesn't call stopPropagation() on them.
			// Then after validation succeeds, it triggers a new submit event
			// *only if* the original submit was from a button click.  But
			// the original submit event propagates either way.
			//
			// So our submit event handler would get two submit events if form is valid & submit button was clicked,
			// but one otherwise, whether or not the form is valid.  That's bizarre.
			//
			// FV's official policy is that you should give up on the submit event and listen
			// for their success event instead; however, that requires a lot of special-FV-handling
			// code, and not all our forms have FV on them.  Either they don't understand
			// the problem well enough, or I don't understand their special needs yet ...
			//
			// FormView wants to get one event if validation succeeds, no events otherwise.
			// It wants to remain agnostic about where the event came from, and
			// whether or not fv is enabled.
			//
			// Here we override FV, to have it stopPropagation on the original event if
			// fv already detected a button click.
			//
			// TODO: test in all browsers, with all three submission techniques: click submit,
			// trigger submit event, or call view.submit().
			//
			// Tested in Chrome, works fine.
			// Tested in Safari, works fine.
			$form.off('submit.fv').on('submit.fv', (e) => {
				// fv does this.
				e.preventDefault();
				// fv doesn't do this.
				instance.$submitButton && e.stopPropagation();
				instance.validate();
			});

			// Let the view know
			this.trigger('validation:set', this, instance);
		});
	},

	/**
	 * Set an individual form value.
	 * @method
	 * @param {string} input Input name
	 * @param {string} value New input value
	 */
	setValue(input, value) {
		this.$(':input').filter(`[name="${input}"]`).val(value);
	},

	/**
	 * Ok, there are some issues with trying to be agnostic and having input/select ui plugins that hide the original element
	 * you still want that hidden element to be validated but it is almost impossible to know (without modifying code each time you add a plugin)
	 * if the element was hidden intentionally (like we do in attribute forms) and when an element was hidden to manipulate the ui only.
	 *
	 * Solution: rather than calling hide/show/toggle directly on the elements in the view- pass the elements through here.
	 * It also handles bulk hiding (the validator will look at parents for the data-nx-ignore="validation" attribute as well)
	 * If validation is not turned on for whatever reason the element passed will be sent to where it would have gone
	 * @param {element} input The jQuery element to hide
	 * @fires module:app#app:resize
	 */
	showField(...input) {
		const inputs = [...input];
		const $form = this.$form;
		const plugins = this.Plugins;
		const validation = this.validator.enabled;

		_.each(inputs, (input) => {
			let $input = $(input);
			if (!$input.is(':input')) {
				$input = $input.find(':input');
			}

			let $formGroup = $input.closest('.form-group');
			if (!$formGroup.length) {
				$formGroup = $input;
			}

			$input.nxUnignore('validation');
			$formGroup.show();

			if (validation) {
				_.each($input, (el) => {
					el.name &&
						plugins.Invoke('validation', $form, 'resetField', [el.name]);
				});
			}
		});
	},

	/**
	 * this.submit() submits the current form by triggering the event this.eventStr + ":confirmed" on dispatcher,
	 * with the arguments returned by this.submitArgs(). A controller should catch this event
	 * and perform whatever steps are necessary.
	 *
	 * @method
	 * @param {method} submit Submit method
	 * @param {event} e Submit event
	 * @fires (this.eventStr):cancelled
	 * @fires (this.eventStr):confirmed
	 */
	submit(e) {
		// The cake stops here:
		if (e && e.preventDefault) {
			e.preventDefault();
			e.stopPropagation();
		} else {
			// There was no event argument, so we weren't called by an event handler.
			// Instead, other code called submit() directly.
			// However, that circumvents any form validation that might be present.
			// So we re-trigger a new submit event, which will call this method again
			// if validation succeeds.
			//
			// In other words, there isn't & should not be a difference between calling
			// this method and triggering a submit event.  We are agnostic, and that is good!
			return this.$('form:first').trigger('submit');
		}

		// Yes, the form may be valid ... but what if we disabled the submit for other reasons?
		if (!this.submitReady()) {
			return this.checkForm();
		}

		// Let it be known
		this.submitted = true;

		// Track our submit
		if (this.submitting && this.submitting.state() === 'pending') {
			this.submitting.rejectWith(this);
		}
		this.submitting = new $.Deferred();

		const confirmed = () => {
			this.$el.addClass('form-submitting');

			// Just submit it
			// Disable submit buttons
			this.$('[type="submit"]').disable();

			// DAM-15747 if there is a confirmMessage, then let
			// confirmView trigger the :confirmed event, no doubling down
			if (!this.confirmMessage) {
				dispatcher.trigger.apply(
					dispatcher,
					[this.eventStr + ':confirmed'].concat(this.submitArgs.apply(this)),
				);
			}
			this.$('.form-submitting').show();
		};

		const cancelled = () => {
			if (!this.submitting) return;
			this.submitting.rejectWith(this);
			delete this.submitting;
		};

		if (this.confirmMessage) {
			// Confirm before submit:
			// If we have a confirmation message, fire a confirmation modal
			const cancelEventStr = `${this.eventStr}:cancelled`;
			// We do want fire our `<this.eventStr>:confirmed` event twice
			const confirmedEventStr = `${this.eventStr}:confirmed`;

			// custom listener methods are safe to remove
			const confirmedMethod = () => {
				dispatcher.off(cancelEventStr, cancelledMethod);
				confirmed();
			};
			const cancelledMethod = () => {
				dispatcher.off(confirmedEventStr, confirmedMethod);
				// Re-enable etc
				this.checkForm();
				cancelled();
			};
			dispatcher.once(confirmedEventStr, confirmedMethod);
			dispatcher.once(cancelEventStr, cancelledMethod);

			dispatcher.trigger.apply(
				dispatcher,
				[
					'app:confirm',
					_.extend(
						{
							message: _.isFunction(this.confirmMessage)
								? this.confirmMessage()
								: this.confirmMessage,
							yesEvent: confirmedEventStr,
							noEvent: cancelEventStr,
						},
						this.confirmViewOpts,
					),
				].concat(this.submitArgs()),
			);
		} else {
			// just do it!
			confirmed();
		}

		return false;
	},

	/**
	 * this.submitArgs() can be overriden to return an array of whatever args you want to pass.
	 * by default we return three args: an object with all the input form values, our model or collection,
	 * and this.args (which may be undefined)
	 * @method
	 * @return {array} Two element array: object containing input form values, and this.args
	 */
	submitArgs() {
		const formValues = this.formValues();
		let _formValues = {};

		// Unescape our from value keys as they were escaped via the name when injecting attributes
		_.reduce(
			formValues,
			(result, value, key) => {
				result[unescapeAttributeName(key)] = value;
				return result;
			},
			_formValues,
		);

		return [_formValues, this.model || this.collection].concat(this.args);
	},

	/**
	 * submitReady() tests if the form can be submitted yet.
	 * There is a wrapper above for this that checks form validation and the result of this method
	 * Returns true by default; can be overridden with any submit-enable test you like.
	 * @method
	 * @return {boolean} Status of the submit action: ready or not.
	 */
	submitReady() {
		const validation = this.Plugins.GetInstance('validation');
		return (
			(!validation || validation.isValid() !== false) &&
			(!this.requiresChanges || this.formChanged)
		);
	},

	/**
	 * Is called when <this.eventStr>:success is triggered
	 * @method
	 * @param {Netx.Model|Netx.Collection} thing Netx.Model or Netx.Collection
	 * @param {boolean} isNew Whether the model was new or not
	 */
	success(thing) {
		// Check that the success was for us (comes from <eventStr>:success)
		// Our pattern has been to have the model/collection as the first argument passed back from the success event
		if (thing) {
			if (this.model && thing.constructor.__isModel__) {
				if (thing !== this.model) {
					return;
				}
			} else if (this.collection && thing.constructor.__isCollection__) {
				if (thing !== this.collection) {
					return;
				}
			}
		}

		// Let the form know it is no longer changed (as data has most likely been saved)
		this.formChanged = false;
		// Resolve submitting promise and pass back any arguments that may have come with the success
		if (this.submitting) {
			this.submitting.resolveWith(this, _.rest(arguments));
			delete this.submitting;
		}
		// Hide on submit
		if (this.hideOnSubmit) {
			if (!this.removeOnHide) {
				// Reset form
				if (this.resetOnSubmit) this.reset();
			}
			this.hide();
			this.stopListening(dispatcher, `${this.eventStr}:success`, this.success);
		}
		// Remove on submit
		else if (this.removeOnSubmit) {
			// Let hide handle the remove in case there are any theme related animations, etc
			// so set this.removeOnHide to true and call this.hide
			this.removeOnHide = true;
			this.hide();
			this.stopListening(dispatcher, `${this.eventStr}:success`, this.success);
		}
		// Nothing
		else {
			// Reset form
			if (this.resetOnSubmit) this.reset();
			// Reset validation
			if (this.validator.enabled) {
				this.setFormValidation();
				this.checkForm();
			}
		}

		this.formChanged = false;
		this.$el.removeClass('form-submitting');
		this.$('.form-submitting').hide();
	},

	/**
	 * Ok, there are some issues with trying to be agnostic and having input/select ui plugins that hide the original element
	 * you still want that hidden element to be validated but it is almost impossible to know (without modifying code each time you add a plugin)
	 * if the element was hidden intentionally (like we do in attribute forms) and when an element was hidden to manipulate the ui only.
	 * Solution: rather than calling hide/show/toggle directly on the elements in the view- pass the elements through here.
	 * It also handles bulk hiding (the validator will look at parents for the data-nx-ignore="validation" attribute as well)
	 * If validation is not turned on for whatever reason the element passed will be sent to where it would have gone
	 * @param {element} input The jQuery element to hide
	 * @param {boolean} bool  Hide/show boolean
	 */
	toggleField(input, bool) {
		this[bool === true ? 'showField' : 'hideField'](input);
	},

	/**
	 * Helper for validating field
	 * @method
	 * @param {element|string} input Input element or `name`
	 */
	validateField(input) {
		// validateField does not seem to do much in this case
		// but revalidateField gets us what we want
		this.validator.enabled &&
			this.Plugins.Invoke('validation', this.$form, 'revalidateField', [input]);
	},

	//
	// HELPERS
	// When some plugins just can't get the field to fire a correct change
	//
	/**
	 * datepicker just doesn't seem to fire the change event formValidation needs properly
	 * @method
	 */
	datepickerChanged(e) {
		// TODO: The plugin fires a change event when the date picker is first shown... cannot seems to find a clean way to get around it
		// aside from editing their code.
		// It is in the `update` method and at the very bottom...
		this.validateField($(e.target));
	},

	/**
	 * select2 multiple just doesn't seem to fire the change event formValidation needs properly
	 * @method
	 */
	select2changed(e) {
		this.validateField($(e.target));
	},

	// /**
	//  * Before child views are attached, search for form elements that will need validation attached.
	//  */
	// hook_preChildren: function(){
	// 	this._forms = this.$('form[data-nx-validate-form]');
	// },
};

export default function (target) {
	if (!target || target.hasFunctionality(NAME)) return target;

	target.extendFunctionality(Props);
	target.__isForm__ = true;

	target.addInitializer((instance, opts = {}) => {
		instance.isForm = true;
		instance.$el.addClass('form-view');
		instance.events = {
			submit: 'submit',
			'click .nx-cancel:first': 'cancel',
			'click [data-nx-control="close"]:first': 'cancel',
			'change .select2-hidden-accessible': 'select2changed',
			'change [data-provide="datepicker"]': 'datepickerChanged',
			change: 'inputChanged',
			input: 'inputChanged',
			// Used to check if we need to lead the user to another invalid field
			'blur input, select, textarea': 'blurred',
			// Used to check if we need to lead the user to another invalid field
			'keydown input': 'keydown',
			'keyup textarea': 'inputChanged',
			'click select': 'inputChanged',
			'nx.fieldAdded :input': 'fieldAdded',
			'nx.fieldMutated :input': 'fieldMutated',
			'nx.fieldMutating :input': 'fieldMutating',
			'nx.fieldRemoved :input': 'fieldRemoved',
			...instance.events,
		};

		/** @override */
		instance.setModel = _.wrap(instance.setModel, (setModel, model, opts) => {
			// flush our cache when our model changes
			instance.flushCachedFormValues();
			instance.formChanged = false;

			return setModel.call(instance, model, opts);
		});

		/** @override */
		instance.show = _.wrap(instance.show, (show) => {
			if (!instance.isShown && instance.resetOnShow) {
				instance.reset();
			}
			return show.call(instance);
		});

		if (!instance.unsavedChanges) {
			instance.unsavedChanges = (unsavedChanges, noProxy) => {
				// First check us:
				// We are a `formView` AND
				// We have changed OR
				// We have a model and it is new
				if (
					instance.formChanged ||
					(instance.model && instance.model.isNew())
				) {
					return instance;
				}
			};
		}

		/**
		 * Alternately, this.eventStr is the prefix for both the submit & cancel events generated by the form.
		 * For instance, if eventStr == 'module:foo',
		 * form sumission will trigger 'module:foo:confirmed' on the dispatcher, and cancel will trigger 'module:foo:cancelled'.
		 * @type {string}
		 * @default
		 */
		instance.eventStr = opts.eventStr || 'module:foo';

		/**
		 * this.confirmMessage contains a confirmation string that pops up in a confirmView before submission.
		 * If you don't want any confirmation pop-up, undefine this property.
		 * @type {string}
		 * @default
		 */
		instance.confirmMessage = opts.confirmMessage || false; //'Warning! This process cannot be undone.  Please confirm you want to execute this job on these assets.'

		/**
		 * Should we diable caching values at `preRender`?
		 * @type {boolean}
		 * @default
		 */
		instance.disableCache = opts.disableCache === true;

		instance.hideOnSubmit = opts.hideOnSubmit === true;

		instance.removeOnSubmit = opts.removeOnSubmit === true;

		instance.revalidateFieldOnAdd = opts.revalidateFieldOnAdd === true;

		/**
		 * resetOnShow determines whether this.reset() will be called when the form is
		 * first shown.  Reset will only be called when this.show() is called on a form that
		 * is not yet visible.
		 * @type {boolean}
		 * @default
		 */
		instance.resetOnShow = opts.resetOnShow === true;

		/**
		 * Signifies that not only must a form be valid to submit but a change must have been made as well
		 * @type {boolean}
		 */
		instance.requiresChanges = opts.requiresChanges !== false;

		/**
		 * Whether to reset form on submit
		 * @type {boolean}
		 */
		instance.resetOnSubmit = opts.resetOnSubmit !== false;

		/**
		 * Custum validation
		 * @memberOf Netx.Views.FormView
		 * @alias validatorFields
		 * @type {object}
		 */
		instance.validatorFields = opts.validatorFields || {};

		/**
		 * Options for confirmViewModal when triggering a confirm on form submit
		 * @memberOf Netx.Views.FormView
		 * @alias confirmViewOpts
		 * @type {Object}
		 */
		instance.confirmViewOpts = opts.confirmViewOpts || {};

		/**
		 * Error cache
		 * Stores invalid fields when validation is turned on
		 * @memberOf Netx.Views.FormView
		 * @alias errorCache
		 * @type {Netx.Collection}
		 */
		instance.errorCache = new CoreCollection([]);

		/**
		 * Error cache view
		 * If your template has an element with [data-nx-control="validation-errors"], then errors will be listed there automatically on a bad submit
		 * @memberOf Netx.Views.FormView
		 * @alias errorCacheView
		 * @type {Netx.Views.FormErrorListView}
		 */
		instance.errorCacheView = instance.addSubView(
			new FormErrorListView({
				collection: instance.errorCache,
			}),
		);

		/**
		 * Form validator object
		 * @memberOf Netx.Views.FormView
		 * @alias validator
		 * @type {object}
		 */
		instance.validator = _.extend(
			{
				enabled: false,
				plugin: null,
				fields: {},
			},
			opts.validator,
		);

		/**
		 * @property {object} Plugins             - Plugins for this view
		 * @property {object} Plugins.validation  - Validation
		 */
		instance.Plugins.Register({
			validation: {
				name: 'formValidation',
				options: {
					message: i18n.t('i18n.valueIsNotValid'),
					excluded: [
						':disabled',
						($field, validator) => {
							return $field.nxIgnores('validation');
						},
					],
					fields:
						_.isObject(instance.validator) &&
						_.isObject(instance.validator.fields)
							? instance.validator.fields
							: {},
					framework: 'bootstrap',
				},
			},
		});

		// If form values were passed, set them at render
		if (opts.form) {
			instance.rendered.then(() => {
				instance.setForm(opts.form);
			});
		}

		// Hey, listen!
		instance
			.on('preRenderHook', instance.prepareForm)
			.on('postRenderHook', () => {
				instance.renderForm();
				// We do not want to setFormValidation before the view has ran all the code
				// in its `postRender`
				_.defer(() => {
					// Set form validation
					instance.setFormValidation();
				});
			})
			.listenTo(dispatcher, `${instance.eventStr}:success`, instance.success)
			.listenTo(dispatcher, `${instance.eventStr}:error`, instance.failure)
			.listenTo(
				instance,
				'validation:err.form.fv',
				instance.formValidationFailure,
			)
			.listenTo(
				instance,
				'validation:err.field.fv',
				instance.fieldValidationFailure,
			)
			.listenTo(
				instance,
				'validation:success.form.fv',
				instance.formValidationSuccess,
			)
			.listenTo(
				instance,
				'validation:success.field.fv',
				instance.fieldValidationSuccess,
			);

		// HTML-948: if the formview has .dispatcherSubmitEvent set, trigger form submit when dispatcher triggers that event
		if (instance.dispatcherSubmitEvent) {
			instance.listenTo(
				dispatcher,
				instance.dispatcherSubmitEvent,
				instance.submit,
			);
		}
		if (instance.viewCollection) {
			// Each `childView` could have its own set inputs - when they render we need to:
			// Cache queries
			// Check the form (enable/disable submit) if applicable
			instance.listenTo(
				instance.viewCollection,
				'view:rendered',
				_.debounce(() => {
					instance.cacheQueries();
					instance.checkForm();
				}, 50),
			);
		}
	}, NAME);

	return target;
}
